import { motion } from 'framer-motion'
import { Check, Expand, ImageOff } from 'lucide-react'
import { forwardRef, useState } from 'react'
import styles from './ImageSelectField.module.css'
import { MediaLightbox } from '@/components/ui/media-lightbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/lib/utils'

interface ImageSelectFieldProps {
  options: string[]
  optionImages: Record<string, string>
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  fieldId: string
}

export const ImageSelectField = forwardRef<HTMLDivElement, ImageSelectFieldProps>(function ImageSelectField(
  { options, optionImages, value, onChange, disabled, fieldId },
  ref,
) {
  const [previewOption, setPreviewOption] = useState<string | null>(null)
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())
  const previewUrl = previewOption ? optionImages[previewOption] : null

  return (
    <>
      <RadioGroup
        ref={ref}
        tabIndex={-1}
        value={value}
        onValueChange={onChange}
        disabled={disabled}
        className={cn(styles.grid, 'focus:outline-none')}
      >
        {options.map((option) => {
          const imageUrl = optionImages[option]
          const isSelected = value === option
          const hasFailed = failedImages.has(option)

          return (
            <label
              key={option}
              className={cn(styles.card, isSelected && styles.cardSelected, disabled && styles.cardDisabled)}
            >
              <RadioGroupItem value={option} id={`${fieldId}-${option}`} className="sr-only" />
              <div className={styles.imageWrapper}>
                {imageUrl && !hasFailed ? (
                  <>
                    <img
                      src={imageUrl}
                      alt={option}
                      className={styles.image}
                      loading="lazy"
                      onError={() =>
                        setFailedImages((prev) => {
                          if (prev.has(option)) return prev
                          const next = new Set(prev)
                          next.add(option)
                          return next
                        })
                      }
                    />
                    <button
                      type="button"
                      className={styles.expandBtn}
                      aria-label={`Preview ${option}`}
                      onClick={(e) => {
                        e.preventDefault()
                        setPreviewOption(option)
                      }}
                    >
                      <Expand size={14} />
                    </button>
                  </>
                ) : (
                  <div className={styles.imageFallback}>
                    <ImageOff size={24} />
                  </div>
                )}
              </div>
              <div className={styles.label}>{option}</div>
              {isSelected && (
                <div className={styles.checkmark}>
                  <Check size={12} strokeWidth={3} />
                </div>
              )}
            </label>
          )
        })}
      </RadioGroup>

      {previewUrl && (
        <MediaLightbox
          open={previewOption !== null}
          onOpenChange={(open) => {
            if (!open) setPreviewOption(null)
          }}
          ariaLabel="Image preview"
        >
          <motion.img
            src={previewUrl}
            alt={previewOption!}
            className="max-h-[85vh] max-w-[90vw] rounded-xl object-contain"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
        </MediaLightbox>
      )}
    </>
  )
})
